<template>
    <span>
        <span
            v-show="(prescription && (prescription.status || prescription.status === 0) && prescription.status != 4)">
            <b-button v-if="!!(
                prescription &&
                prescription.rxNumber &&
                prescription.rfNumber != null
            )
            " v-b-modal="'transfer-rx'" :disabled="disableInternalTransfer" variant="success">
                Internal Transfer
            </b-button>
            <b-button v-b-modal="'external-transfer-modal'" variant="success">
                External Transfer
            </b-button>
        </span>
        <span v-show="prescription && prescription.rxNumber && prescription.aspnRxID && prescription.isReturnableToAsembia">
            <b-button variant="warning" @click="returnToAsembia">
                Return to Asembia
            </b-button>
        </span>

        <!-- Internal Transfer Modal -->
        <b-modal id="transfer-rx" hide-footer no-close-on-backdrop title="Transfer Rx to:">
            <b-row>
                <b-col>
                    <SearchComponent :id="newStore && +newStore.id" v-model="newStore" :label="`New Store`"
                        :prepopulate="true" :search-u-r-l="`/Store`" />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-button block class="mt-3" @click="$bvModal.hide('transfer-rx')">
                        Close
                    </b-button>
                </b-col>
                <b-col>
                    <b-button block class="mt-3" variant="success" @click="transferRx()">
                        Transfer
                    </b-button>
                </b-col>
            </b-row>
        </b-modal>

        <!-- External Transfer Modal -->
        <ExternalTransferModal
            :prescriptions="[prescription]"
            :store="store"
            :patient="patient"
            @transferred="$emit('transferred', $event)" />

        <!-- TransferMyRx cancel reason -->
        <b-modal id="cancel-reason-modal" title="Cancel Transfer" @ok="cancelTransferMyRxWithReason" @hidden="cancelReason = ''">
            <b-form-group label="Reason" label-for="reason-input">
                <b-form-input id="reason-input" v-model="cancelReason" />
            </b-form-group>
        </b-modal>
    </span>
</template>

<script lang="ts">
// Transfer UI cluster, extracted from PrescriptionDetailsPage: the internal/external transfer
// buttons + their modals, Return to Asembia, and the TransferMyRx status probe + cancel-reason
// modal. The TransferMyRx endpoints and the internal-transfer POST ride raw axios inside this
// component (single-consumer endpoints — servicing them buys nothing), so this component is not
// gated on any service PR.
//
// Contract: read-only entity props in, scoped events up. The child never replaces the prescription
// object — every transfer path emits and lets the container do the entity replacement, preserving
// each path's existing container follow-up:
//   internal-transferred(rx) → container assigns + updates the route (no refetch, matches today)
//   transferred(rx)          → container assigns + refetches + updates alerts (external transfer)
//   rx-needs-refresh(rx)     → container refetches (Asembia return)
//   status-changed(status)   → container mirrors transferMyRxStatus so PrescriptionFormFields'
//                              status banner + Cancel Transfer button keep rendering off form-state
//   locked(data)             → container shows the 418 locked-entity message via its LockHandler
// No .sync props; no $permissions (disableInternalTransfer arrives computed from the container).
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import Axios, { AxiosError } from "axios";
import { Prescription } from "@/models/Prescription";
import { Store } from "@/models/Store";
import { Patient } from "@/models/Patient";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { PrescriptionService } from "@/services/PrescriptionService";
import SearchComponent from "@/components/SearchComponent.vue";
import ExternalTransferModal from "@/components/Transfer/ExternalTransferModal.vue";

@Component({
    components: {
        SearchComponent,
        ExternalTransferModal,
    },
})
export default class RxTransferActions extends Vue {
    @Prop({ required: true }) prescription!: Prescription;
    @Prop({ required: true }) store!: Store;
    @Prop({ required: true }) patient!: Patient;
    @Prop({ default: false }) disableInternalTransfer!: boolean;

    private newStore: Store = new Store();
    private cancelReason: string = "";

    // Was a data-watch on the page (registered at page creation, before the prescription loaded);
    // as a path-watch on a by-reference prop it must be immediate so the already-delivered
    // prescription's transferMyRxId is probed at child creation rather than only on a later change.
    @Watch("prescription.transferMyRxId", { immediate: true })
    async getTransferMyRxStatus() {
        // Clear first so the banner never carries a previous prescription's state while the probe
        // is in flight; this is also the safe fallback if the probe below fails.
        this.setTransferMyRxStatus("");

        const transferMyRxId = this.prescription?.transferMyRxId;
        if (!transferMyRxId) return;

        try {
            const newResp = await Axios.get<any>(`/api/TransferMyRx/${transferMyRxId}`);

            // Staleness guard: rapid transferMyRxId changes fire concurrent GETs. If the id moved
            // on while this request was in flight, drop the stale response rather than emit its
            // status over the currently-shown prescription's.
            if (this.prescription?.transferMyRxId !== transferMyRxId) return;

            if (!newResp.data) return;

            const json = JSON.parse(newResp.data.jsonBody);
            this.setTransferMyRxStatus(json.status);
        } catch (error) {
            // Leave the status cleared (set to "" above) so the banner does NOT falsely offer a
            // Cancel Transfer button for a transfer whose state we could not read. Surface, don't swallow.
            this.$notification(
                NotificationOptions.error(error, "Transfer Status"),
            );
            console.error("TransferMyRx status probe failed - ", error);
        }
    }

    private setTransferMyRxStatus(status: string) {
        // Write-only from this component's perspective — the container mirrors it onto the page and
        // feeds PrescriptionFormFields. Emit only; keep no local copy that could drift.
        this.$emit("status-changed", status);
    }

    transferRx() {
        // Guard against transferring with no store picked: newStore starts as a fresh Store() whose
        // id is undefined, which would POST to /Store/undefined/transfer. Keep the modal open so the
        // user can choose a store rather than silently closing it on a no-op.
        if (!this.newStore?.id) {
            this.$notification(
                NotificationOptions.warning("Please select a store to transfer to."),
            );
            return;
        }

        Axios.post(`/Store/${this.newStore.id}/transfer`, this.prescription)
            .then((response) => {
                // The container owns entity replacement + the route update; do not mutate the prop.
                this.$emit("internal-transferred", response.data);
                this.$notification(
                    NotificationOptions.successSaveNotificationPreset("Prescription Transferred"),
                );
            })
            .catch((error: AxiosError) => {
                if (error) {
                    this.$notification(
                        NotificationOptions.errorSaveNotificationPreset("Internal Transfer", error),
                    );
                    console.error("Internal Transfer - ", {
                        error,
                        response: error?.response,
                    });
                }
            });
        this.$bvModal.hide("transfer-rx");
    }

    returnToAsembia() {
        const rx = this.prescription;
        if (!rx?.aspnRxID || !rx.rxNumber || rx.rfNumber == null) return;

        this.$bvModal
            .msgBoxConfirm(
                "Return this prescription to Asembia? It will be marked as transferred out locally and Asembia will be notified.",
                {
                    title: "Confirm Return to Asembia",
                    size: "sm",
                    buttonSize: "sm",
                    okVariant: "warning",
                    okTitle: "YES",
                    cancelTitle: "NO",
                    footerClass: "p-2",
                    hideHeaderClose: false,
                    centered: true,
                },
            )
            .then(async (value) => {
                if (!value) return;
                try {
                    await PrescriptionService.returnToAsembia(rx.storeID, rx.rxNumber, rx.rfNumber);
                    this.$emit("rx-needs-refresh", rx);
                    this.$notification(
                        NotificationOptions.notificationOptionsPreset(
                            "Prescription returned to Asembia.",
                            NotificationOptions.NotificationTypes.success,
                        ),
                    );
                } catch (err) {
                    const error = err as AxiosError;
                    const message = (error.response?.data as string) || error.message || "Failed to return prescription to Asembia.";
                    this.$notification(
                        NotificationOptions.notificationOptionsPreset(
                            message,
                            NotificationOptions.NotificationTypes.danger,
                        ),
                    );
                }
            })
            .catch((err) => {
                console.error("Error caught on returnToAsembia.", err);
            });
    }

    async cancelTransferMyRxWithReason() {
        try {
            await Axios.post(`/api/TransferMyRx/cancel/${this.prescription.transferMyRxId}`, {}, {
                params: {
                    reason: this.cancelReason
                }
            });
            this.$notification(
                NotificationOptions.success("Transfer canceled."),
            );
            // Re-probe so the status banner + Cancel Transfer button reflect the now-canceled state
            // instead of the stale "pending" state, which would otherwise invite a duplicate cancel.
            await this.getTransferMyRxStatus();
        } catch (err) {
            const error = err as AxiosError;
            if (error.response?.status === 418) {
                this.$emit("locked", error.response.data);
            } else {
                this.$notification(NotificationOptions.error(error));
            }
        }
    }
}
</script>
